/* eslint-disable react-hooks/exhaustive-deps */
import {
  LDClient,
  LDProvider,
  useLDClient,
  // basicLogger,
} from "launchdarkly-react-client-sdk"
import { useState, useEffect, ReactNode, memo } from "react"
import { localStorageKey } from "constants-lib/localStorage"
import {
  UserContext,
  SessionContext,
  ApplicationContext,
  mapUserContext,
  mapSessionContext,
  mapApplicationContext,
} from "./utils/contextMapper"
import ldLogRocketLifecycleHook from "./utils/hooks"

const IP_INFO_API = "https://api.ipify.org?format=json"

type Context = {
  kind: string
  user?: UserContext
  session?: SessionContext
  application?: ApplicationContext
}

type LDAppWrapperProps = {
  children: ReactNode
  user: {
    email: string
    userId: string
  }
  app: {
    name: string
    version: string
  }
}

type IPInfo = {
  ip: string | null
}

/**
 * A React functional component that updates the LaunchDarkly client context.
 *
 * This component uses the `useLDClient` hook to retrieve the LaunchDarkly client instance
 * and updates its context whenever the `context` prop changes. The `identify` method
 * is called on the LaunchDarkly client to update the context, and any errors during
 * this process are logged to the console.
 *
 * @param {Object} props - The component props.
 * @param {Context} props.context - The LaunchDarkly context to be identified.
 *
 * @returns {null} This component does not render any visible UI.
 */
const UpdateLDContext: React.FC<{ context: Context }> = memo(({ context }) => {
  const [isContextUpdatedWithIP, setIsContextUpdatedWithIP] = useState(false)

  const ldClient = useLDClient()

  function getAndSetIsWMInternalUser(client: LDClient) {
    const isWMInternalUser = client.variation(
      "LDFFChatbotwminternal",
      false,
    ) as boolean

    try {
      localStorage.setItem(
        localStorageKey.IS_WM_INTERNAL_USER,
        String(isWMInternalUser),
      )
    } catch (error) {
      console.error("Error setting isWMInternalUser in localStorage:", error)
    }
  }

  async function identifyAsync(client: LDClient, clientContext: Context) {
    await client
      .identify(clientContext)
      .catch((error) => console.error("Error updating context: ", error))

    if (clientContext.session?.ip && !isContextUpdatedWithIP) {
      setIsContextUpdatedWithIP(true)
    }
  }

  useEffect(() => {
    // ensure there is at least a session context before updating the context in LD
    // I removed the dependency on `context.application` to prevent issues with versioning from preventing the context from being updated
    if (ldClient && context.session) {
      identifyAsync(ldClient, context).catch((error) =>
        console.error("Error in identifyAsync: ", error),
      )
    }
  }, [ldClient, context])

  useEffect(() => {
    if (ldClient && isContextUpdatedWithIP) {
      getAndSetIsWMInternalUser(ldClient)
    }
  }, [isContextUpdatedWithIP])

  return null
})
/**
 * LDAppWrapper is a React functional component that wraps its children with LaunchDarkly's
 * feature flag provider, allowing dynamic feature flagging based on user, session, and application contexts.
 *
 * @component
 * @param {LDAppWrapperProps} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child components to be rendered within the LaunchDarkly provider.
 * @param {object} props.user - The user context containing user-specific information.
 * @param {string} props.user.userId - The unique identifier for the user.
 * @param {string} [props.user.email] - The email address of the user (optional).
 * @param {object} props.app - The application context containing app-specific information.
 * @param {string} props.app.name - The name of the application.
 * @param {string} props.app.version - The version of the application.
 *
 * @returns {JSX.Element} A JSX element wrapped with the LaunchDarkly provider.
 *
 * @remarks
 * - This component initializes and updates the LaunchDarkly context dynamically based on session, user, and application data.
 * - It fetches the user's IP address for session context and generates a unique session ID if one does not exist.
 * - User and application contexts are updated when their respective props change.
 *
 * @example
 * ```tsx
 * <LDAppWrapper
 *   user={{ userId: "12345", email: "user@example.com" }}
 *   app={{ name: "MyApp", version: "1.0.0" }}
 * >
 *   <MyComponent />
 * </LDAppWrapper>
 * ```
 *
 * See packages/component-lib/LaunchDarkly/components/TestLDComponent/TestLDComponent.tsx
 * for an example of how to use the LDClientWrapper with both functional and class components
 */
const LDAppWrapper: React.FC<LDAppWrapperProps> = ({ children, user, app }) => {
  const [context, setContext] = useState<Context>({
    kind: "multi",
  })

  const fetchUserIP = async (): Promise<IPInfo> => {
    let response
    let data: IPInfo = { ip: null }

    try {
      response = await fetch(IP_INFO_API)
      data = (await response.json()) as IPInfo
    } catch (error) {
      console.error("Error fetching IP information:", error)
    }

    return { ip: data.ip ?? null }
  }

  const updateSessionContext = async () => {
    const { ip } = await fetchUserIP()

    const sessionContext = {
      ip,
    }

    setContext((prevContext) => ({
      ...prevContext,
      session: mapSessionContext(sessionContext),
    }))
  }

  const updateUserContext = (userSettings: LDAppWrapperProps["user"]) => {
    if (userSettings?.userId) {
      const userContext = {
        key: userSettings.userId,
        email: userSettings.email,
        // Add any other user attributes here that we want to capture. Private attributes nested under `_meta` are not shown in the LaunchDarkly dashboard.
      }

      setContext((prevContext) => ({
        ...prevContext,
        user: mapUserContext(userContext),
      }))
    }
  }

  const updateApplicationContext = (appSettings: LDAppWrapperProps["app"]) => {
    if (appSettings?.name && appSettings?.version) {
      const applicationContext = {
        key: `${appSettings.name}-${appSettings.version}`,
        name: appSettings.name,
        version: appSettings.version,
      }

      setContext((prevContext) => ({
        ...prevContext,
        application: mapApplicationContext(applicationContext),
      }))
    }
  }

  useEffect(() => {
    updateSessionContext().catch((error) =>
      console.error("Error updating session context:", error),
    )
  }, [])

  useEffect(() => {
    updateUserContext(user)
    // just listening for user.userId changes, passing in user object causes way too many updates due to shallow comparison
  }, [user?.userId])

  useEffect(() => {
    updateApplicationContext(app)
  }, [app])

  const initialContext = {
    /*
     * Initialize context with session context to prevent LaunchDarkly
     * from creating additional contexts after calling identify with
     * updated IP and other context details.
     */
    kind: "session",
    ...mapSessionContext({}),
  }

  /*
   * Caveats:
   * 1. Having multiple context providers in the same app can cause minor issues due to chatbot injection in other apps.
   *    - This results in two initializations of the provider, leading to two network calls to LaunchDarkly for initialization.
   *    - However, a single connection is still used, so this does not cause any functional issues.
   *    - In fact, some customers have use cases for this behavior.
   * 2. All network calls made to LaunchDarkly using the LDProvider will show the initiator as `useLDClientError.tsx`.
   *    - This is a quirk of the SDK and occurs even when the calls are successful.
   */
  return (
    <LDProvider
      clientSideID={
        (process.env.REACT_APP_LD_CLIENT_ID ||
          process.env.NEXT_PUBLIC_LD_CLIENT_ID) as string
      }
      reactOptions={{ useCamelCaseFlagKeys: false }}
      options={{
        // logger: basicLogger({ level: "info" }),
        hooks: [ldLogRocketLifecycleHook],
      }}
      context={initialContext}
    >
      {children}
      <UpdateLDContext context={context} />
    </LDProvider>
  )
}

export default LDAppWrapper
